import { useState } from "react";
import { isFixedReferral, referralAmount } from "@/utils/referralFee";

const MARKETPLACES = [
  { id: "amazon",   label: "Amazon" },
  { id: "flipkart", label: "Flipkart" },
  { id: "meesho",   label: "Meesho" },
  { id: "myntra",   label: "Myntra" },
  { id: "shopify",  label: "Shopify" },
];

// ─── Per-marketplace calculation helpers ─────────────────────────────────────

function calcAmazon(mp) {
  const item_price   = mp.item_price    || 0;
  const closing_fee  = mp.closing_fee   || 0;
  const fulfilment   = mp.fulfilment_cost || 0;
  const product_tax  = mp.product_tax   || 0;
  const product_cost = mp.product_cost  || 0;
  const rfPct        = mp.referral_fee_pct || 0;

  if (!item_price) return { suggested_sp: 0, net_proceeds: 0, net_profit: 0, profit_margin: 0 };

  const fixed        = isFixedReferral(mp);
  const referral     = referralAmount(mp);
  const taxes_amount = referral * ((mp.tax_on_fees_pct || 0) / 100);

  const net_proceeds  = item_price - referral - closing_fee - fulfilment - taxes_amount - product_tax;
  const net_profit    = net_proceeds - product_cost;
  const profit_margin = item_price > 0 ? (net_profit / item_price) * 100 : 0;

  const suggested_sp = product_cost > 0 && (fixed || rfPct < 100)
    ? fixed
      ? Math.ceil((product_cost + fulfilment + closing_fee + product_tax + referral) / (1 - 0.17))
      : Math.ceil((product_cost + fulfilment + closing_fee + product_tax) / (1 - rfPct / 100 - 0.17))
    : item_price;

  return {
    suggested_sp:  Math.round(suggested_sp),
    net_proceeds:  Math.round(net_proceeds  * 100) / 100,
    net_profit:    Math.round(net_profit    * 100) / 100,
    profit_margin: Math.round(profit_margin * 10)  / 10,
  };
}

function calcFlipkart(mp) {
  const bank_settlement = mp.bank_settlement || 0;
  const logistics_fees  = mp.logistics_fees  || 0;
  const listing_price   = mp.listing_price   || 0;
  const net_profit      = bank_settlement > 0 ? bank_settlement - logistics_fees : 0;
  const profit_margin   = listing_price   > 0 ? (net_profit / listing_price) * 100 : 0;
  return {
    suggested_sp:  listing_price,
    net_profit:    Math.round(net_profit    * 100) / 100,
    profit_margin: Math.round(profit_margin * 10)  / 10,
  };
}

function calcMyntra(mp) {
  const selling_price  = mp.selling_price  || 0;
  const total_fees     = (mp.pick_pack_fee  || 0) + (mp.fixed_fee      || 0)
                       + (mp.commission     || 0) + (mp.collection_fee || 0)
                       + (mp.gt_charge      || 0);
  const net_profit    = selling_price > 0 ? selling_price - total_fees : 0;
  const profit_margin = selling_price > 0 ? (net_profit / selling_price) * 100 : 0;
  return {
    suggested_sp:  selling_price,
    net_profit:    Math.round(net_profit    * 100) / 100,
    profit_margin: Math.round(profit_margin * 10)  / 10,
    total_fees,
  };
}

function calcShopify(mp) {
  const selling_price = mp.selling_price || 0;
  const net_profit    = selling_price > 0
    ? selling_price - (mp.cost_price || 0) - (mp.shopify_tax || 0) - (mp.customer_acquisition_cost || 0)
    : 0;
  const profit_margin = selling_price > 0 ? (net_profit / selling_price) * 100 : 0;
  return {
    suggested_sp:  selling_price,
    net_profit:    Math.round(net_profit    * 100) / 100,
    profit_margin: Math.round(profit_margin * 10)  / 10,
  };
}

function calcMeesho(mp) {
  const { item_price = 0, referral_fee_pct = 0, fulfilment_cost = 0, tax_on_fees_pct = 0, other_cost = 0, product_cost = 0 } = mp;
  if (!item_price) return { suggested_sp: 0, net_profit: 0, profit_margin: 0 };
  const fixed    = isFixedReferral(mp);
  const referral = referralAmount(mp);
  const tax      = referral * (tax_on_fees_pct / 100);
  const net_profit    = item_price - referral - tax - fulfilment_cost - other_cost - product_cost;
  const profit_margin = item_price > 0 ? (net_profit / item_price) * 100 : 0;
  const suggested_sp  = product_cost > 0 && (fixed || referral_fee_pct < 100)
    ? fixed
      ? Math.ceil((product_cost + fulfilment_cost + other_cost + referral) / (1 - 0.17))
      : Math.ceil((product_cost + fulfilment_cost + other_cost) / (1 - referral_fee_pct / 100 - 0.17))
    : item_price;
  return {
    suggested_sp:  Math.round(suggested_sp),
    net_profit:    Math.round(net_profit    * 100) / 100,
    profit_margin: Math.round(profit_margin * 10)  / 10,
  };
}

export function calcSP(mp, marketplace) {
  if (marketplace === "amazon")   return calcAmazon(mp);
  if (marketplace === "flipkart") return calcFlipkart(mp);
  if (marketplace === "myntra")   return calcMyntra(mp);
  if (marketplace === "shopify")  return calcShopify(mp);
  return calcMeesho(mp);
}

// ─── Shared UI primitives ─────────────────────────────────────────────────────

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      {children}
    </div>
  );
}

function NumInput({ value, onChange, prefix = "₹", suffix, testId }) {
  return (
    <div className="relative">
      {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs">{prefix}</span>}
      <input
        type="number" min="0" step="0.01"
        value={value || ""}
        onChange={onChange}
        data-testid={testId}
        className={`w-full py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 text-slate-900 ${prefix ? "pl-7" : "pl-3"} ${suffix ? "pr-8" : "pr-3"}`}
      />
      {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs">{suffix}</span>}
    </div>
  );
}

function ReferralFeeField({ currentMP, upd }) {
  return (
    <Field label={isFixedReferral(currentMP) ? "Referral Fee (fixed ₹)" : "Referral Fee (%)"}>
      <div className="flex gap-1.5">
        <select
          value={currentMP.referral_fee_type || "pct"}
          onChange={e => upd("referral_fee_type", e.target.value)}
          data-testid="sp-referral-type"
          className="py-2 px-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-orange-500 text-slate-900 bg-white"
        >
          <option value="pct">%</option>
          <option value="fixed">₹</option>
        </select>
        <div className="flex-1 min-w-0">
          {isFixedReferral(currentMP)
            ? <NumInput value={currentMP.referral_fee_fixed} onChange={e => upd("referral_fee_fixed", e.target.value)} />
            : <NumInput value={currentMP.referral_fee_pct}   onChange={e => upd("referral_fee_pct",   e.target.value)} prefix="" suffix="%" />
          }
        </div>
      </div>
    </Field>
  );
}

// ─── Result panel rows ────────────────────────────────────────────────────────

function ResultRow({ label, value }) {
  return (
    <div className="flex justify-between text-xs">
      <span className="text-slate-500">{label}</span>
      <span className="text-slate-700">₹{typeof value === "number" ? value.toFixed(2) : value || 0}</span>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function SuggestiveSPTab({ data, onChange }) {
  const [activeMP, setActiveMP] = useState("amazon");

  const suggestiveSP = data.suggestive_sp || {};
  const currentMP    = suggestiveSP[activeMP] || {};

  const upd = (field, value) => {
    onChange("suggestive_sp", {
      ...suggestiveSP,
      [activeMP]: { ...currentMP, [field]: field === "referral_fee_type" ? value : Number(value) },
    });
  };

  const result = calcSP(currentMP, activeMP);
  const { suggested_sp, net_profit, profit_margin } = result;
  const net_proceeds = result.net_proceeds ?? null;

  const profitColor = net_profit   > 0 ? "text-green-600" : "text-red-500";
  const marginColor = profit_margin > 0 ? "text-green-600" : "text-red-500";

  return (
    <div>
      <div className="flex items-center gap-2 mb-5">
        <div className="w-5 h-5 bg-orange-100 rounded flex items-center justify-center">
          <span className="text-orange-600 text-xs font-bold">6</span>
        </div>
        <h2 className="text-base font-semibold text-slate-900">Suggestive SP</h2>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200">
          <div className="flex items-start gap-2">
            <div className="w-4 h-4 bg-orange-100 rounded flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-orange-600 text-xs">$</span>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-800">Suggestive Selling Price (Optional)</h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Marketplace commission and cost calculation. Enter values to get estimated selling price, commission and profit.
              </p>
            </div>
          </div>
        </div>

        {/* Marketplace Tabs */}
        <div className="flex border-b border-slate-200 overflow-x-auto">
          {MARKETPLACES.map(mp => (
            <button
              key={mp.id}
              data-testid={`sp-tab-${mp.id}`}
              onClick={() => setActiveMP(mp.id)}
              className={`px-5 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-all ${
                activeMP === mp.id
                  ? "border-orange-500 text-orange-600 bg-orange-50/30"
                  : "border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50"
              }`}
            >
              {mp.label}
            </button>
          ))}
        </div>

        <div className="p-5">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

            {/* ─── Input Fields (left 2 cols) ─── */}
            <div className="lg:col-span-2 grid grid-cols-2 md:grid-cols-3 gap-4">

              {/* ── AMAZON ── */}
              {activeMP === "amazon" && (<>
                <Field label="Item Price (₹)">
                  <NumInput value={currentMP.item_price} onChange={e => upd("item_price", e.target.value)} testId="sp-amazon-item-price" />
                </Field>
                <ReferralFeeField currentMP={currentMP} upd={upd} />
                <Field label="Closing Fee (₹)">
                  <NumInput value={currentMP.closing_fee} onChange={e => upd("closing_fee", e.target.value)} testId="sp-amazon-closing-fee" />
                </Field>
                <Field label="Fulfilment Cost (₹)">
                  <NumInput value={currentMP.fulfilment_cost} onChange={e => upd("fulfilment_cost", e.target.value)} />
                </Field>
                <Field label="Taxes on Fees (%)">
                  <NumInput value={currentMP.tax_on_fees_pct} onChange={e => upd("tax_on_fees_pct", e.target.value)} prefix="" suffix="%" />
                </Field>
                <Field label="Product Tax (₹)">
                  <NumInput value={currentMP.product_tax} onChange={e => upd("product_tax", e.target.value)} testId="sp-amazon-product-tax" />
                </Field>
                <Field label="Cost per unit (₹)">
                  <NumInput value={currentMP.product_cost} onChange={e => upd("product_cost", e.target.value)} testId="sp-amazon-product-cost" />
                </Field>
              </>)}

              {/* ── FLIPKART ── */}
              {activeMP === "flipkart" && (<>
                <Field label="Bank Settlement (₹)">
                  <NumInput value={currentMP.bank_settlement} onChange={e => upd("bank_settlement", e.target.value)} testId="sp-fk-bank-settlement" />
                </Field>
                <Field label="Fees and Taxes (₹)">
                  <NumInput value={currentMP.fees_and_taxes} onChange={e => upd("fees_and_taxes", e.target.value)} testId="sp-fk-fees-taxes" />
                </Field>
                <Field label="Seller Price (₹)">
                  <NumInput value={currentMP.seller_price} onChange={e => upd("seller_price", e.target.value)} testId="sp-fk-seller-price" />
                </Field>
                <Field label="Logistics Fees (₹)">
                  <NumInput value={currentMP.logistics_fees} onChange={e => upd("logistics_fees", e.target.value)} testId="sp-fk-logistics-fees" />
                </Field>
                <Field label="Listing Price (₹)">
                  <NumInput value={currentMP.listing_price} onChange={e => upd("listing_price", e.target.value)} testId="sp-fk-listing-price" />
                </Field>
              </>)}

              {/* ── MEESHO (generic calculation fields) ── */}
              {activeMP === "meesho" && (<>
                <Field label="Item Price (₹)">
                  <NumInput value={currentMP.item_price} onChange={e => upd("item_price", e.target.value)} />
                </Field>
                <ReferralFeeField currentMP={currentMP} upd={upd} />
                <Field label="Fulfilment Cost (₹)">
                  <NumInput value={currentMP.fulfilment_cost} onChange={e => upd("fulfilment_cost", e.target.value)} />
                </Field>
                <Field label="Tax on Fees (%)">
                  <NumInput value={currentMP.tax_on_fees_pct} onChange={e => upd("tax_on_fees_pct", e.target.value)} prefix="" suffix="%" />
                </Field>
                <Field label="Other Cost (₹)">
                  <NumInput value={currentMP.other_cost} onChange={e => upd("other_cost", e.target.value)} />
                </Field>
                <Field label="Product Cost (₹)">
                  <NumInput value={currentMP.product_cost} onChange={e => upd("product_cost", e.target.value)} />
                </Field>
              </>)}

              {/* ── MYNTRA ── */}
              {activeMP === "myntra" && (<>
                <Field label="Current Settlement (₹)">
                  <NumInput value={currentMP.current_settlement} onChange={e => upd("current_settlement", e.target.value)} testId="sp-myntra-settlement" />
                </Field>
                <Field label="Pick & Pack Fee (₹)">
                  <NumInput value={currentMP.pick_pack_fee} onChange={e => upd("pick_pack_fee", e.target.value)} testId="sp-myntra-pick-pack" />
                </Field>
                <Field label="Fixed Fee (₹)">
                  <NumInput value={currentMP.fixed_fee} onChange={e => upd("fixed_fee", e.target.value)} />
                </Field>
                <Field label="Commission (₹)">
                  <NumInput value={currentMP.commission} onChange={e => upd("commission", e.target.value)} testId="sp-myntra-commission" />
                </Field>
                <Field label="Collection Fee (₹)">
                  <NumInput value={currentMP.collection_fee} onChange={e => upd("collection_fee", e.target.value)} />
                </Field>
                <Field label="Seller Price (₹)">
                  <NumInput value={currentMP.seller_price} onChange={e => upd("seller_price", e.target.value)} testId="sp-myntra-seller-price" />
                </Field>
                <Field label="GT Charge (₹)">
                  <NumInput value={currentMP.gt_charge} onChange={e => upd("gt_charge", e.target.value)} testId="sp-myntra-gt-charge" />
                </Field>
                <Field label="Selling Price (₹)">
                  <NumInput value={currentMP.selling_price} onChange={e => upd("selling_price", e.target.value)} testId="sp-myntra-selling-price" />
                </Field>
              </>)}

              {/* ── SHOPIFY ── */}
              {activeMP === "shopify" && (<>
                <Field label="Selling Price (₹)">
                  <NumInput value={currentMP.selling_price} onChange={e => upd("selling_price", e.target.value)} testId="sp-shopify-selling-price" />
                </Field>
                <Field label="Cost Price (₹)">
                  <NumInput value={currentMP.cost_price} onChange={e => upd("cost_price", e.target.value)} testId="sp-shopify-cost-price" />
                </Field>
                <Field label="Tax (₹)">
                  <NumInput value={currentMP.shopify_tax} onChange={e => upd("shopify_tax", e.target.value)} testId="sp-shopify-tax" />
                </Field>
                <Field label="Customer Acquisition Cost (₹)">
                  <NumInput value={currentMP.customer_acquisition_cost} onChange={e => upd("customer_acquisition_cost", e.target.value)} testId="sp-shopify-cac" />
                </Field>
              </>)}
            </div>

            {/* ─── Results Panel ─── */}
            <div className="bg-slate-50 rounded-xl p-4 space-y-4 border border-slate-200">

              {/* Primary price display */}
              <div className="text-center">
                <p className="text-xs text-slate-500 mb-1">
                  {activeMP === "amazon"   ? "Suggested Selling Price (₹)"
                   : activeMP === "flipkart" ? "Listing Price (₹)"
                   : "Selling Price (₹)"}
                </p>
                <p className="text-3xl font-bold text-orange-500" data-testid="sp-suggested-price">
                  {suggested_sp || "—"}
                </p>
              </div>

              {/* Amazon: Net Proceeds */}
              {activeMP === "amazon" && net_proceeds !== null && (<>
                <div className="h-px bg-slate-200" />
                <div className="text-center">
                  <p className="text-xs text-slate-500 mb-1">Net Proceeds (₹)</p>
                  <p className={`text-xl font-bold ${profitColor}`}>
                    {net_proceeds !== 0 ? net_proceeds.toFixed(2) : "—"}
                  </p>
                </div>
              </>)}

              <div className="h-px bg-slate-200" />
              <div className="text-center">
                <p className="text-xs text-slate-500 mb-1">Net Profit (₹)</p>
                <p className={`text-2xl font-bold ${profitColor}`} data-testid="sp-net-profit">
                  {net_profit !== 0 ? net_profit.toFixed(2) : "—"}
                </p>
              </div>

              <div className="h-px bg-slate-200" />
              <div className="text-center">
                <p className="text-xs text-slate-500 mb-1">Profit Margin</p>
                <p className={`text-2xl font-bold ${marginColor}`} data-testid="sp-profit-margin">
                  {profit_margin !== 0 ? `${profit_margin}%` : "—"}
                </p>
              </div>

              {/* Breakdown rows */}
              {activeMP === "amazon" && currentMP.item_price > 0 && (
                <div className="mt-2 space-y-1.5">
                  <ResultRow label="Referral Fee"   value={referralAmount(currentMP)} />
                  {currentMP.closing_fee > 0 && <ResultRow label="Closing Fee"    value={currentMP.closing_fee} />}
                  <ResultRow label="Fulfilment Cost" value={currentMP.fulfilment_cost || 0} />
                  {currentMP.product_tax > 0 && <ResultRow label="Product Tax"    value={currentMP.product_tax} />}
                  <ResultRow label="Cost per unit"  value={currentMP.product_cost || 0} />
                </div>
              )}

              {activeMP === "flipkart" && currentMP.bank_settlement > 0 && (
                <div className="mt-2 space-y-1.5">
                  <ResultRow label="Bank Settlement" value={currentMP.bank_settlement} />
                  <ResultRow label="Logistics Fees"  value={currentMP.logistics_fees || 0} />
                </div>
              )}

              {activeMP === "myntra" && currentMP.selling_price > 0 && (
                <div className="mt-2 space-y-1.5">
                  <ResultRow label="Total Fees" value={result.total_fees} />
                  {currentMP.current_settlement > 0 && <ResultRow label="Settlement" value={currentMP.current_settlement} />}
                </div>
              )}

              {activeMP === "shopify" && currentMP.selling_price > 0 && (
                <div className="mt-2 space-y-1.5">
                  <ResultRow label="Cost Price"     value={currentMP.cost_price || 0} />
                  <ResultRow label="Tax + Acq. Cost" value={(currentMP.shopify_tax || 0) + (currentMP.customer_acquisition_cost || 0)} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
